<template>
    <div class="modal fade" id="confirm-details-modal" tabindex="-1" ref="confirmDetails">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModal"></button>
                <div class="modal-sidebar">
                    <div class="modal-header">
                        <h5 class="modal-title">Confirm your details</h5>
                    </div> 
                    <div class="modal-body">
                        Help us serve you better by updating your details before continuing.
                    </div>
                </div>
                <div class="modal-main">
                    <div class="modal-body" style="display:flex; flex-direction: column; gap: 1rem;" v-if="v$">
                        <Flash v-if="flashMessage" :alert="'danger'" style="white-space: pre-line;">{{ flashMessage }}</Flash>
                        <div>
                            <DropdownInput
                                prependIcon="bi-briefcase"
                                label="Your Role"
                                v-model="connect_user_role_id"
                                :v="v$.connect_user_role_id"
                                :options="roles"
                            />
                        </div>
                        <transition name="fade">
                            <div v-if="showDentistField" class="details-wrapper">
                                <div class="mb-2">
                                    <DropdownInput
                                        prependIcon="bi-person"
                                        label="Your Details"
                                        v-model="dentist_id"
                                        :v="v$.dentist_id"
                                        :options="practiceDentists"
                                    />
                                </div>
                                <div class="mb-2" v-if="dentist_id && showEmailField">
                                    <TextInput
                                        prependIcon="bi-envelope-fill"
                                        label="Email Address"
                                        v-model="email"
                                        :v="v$.email"
                                        :class="{ 'is-invalid': v$.email.$error }"
                                    />
                                </div>
                                <div class="mb-2" v-if="dentist_id && showPhoneField">
                                    <PhoneInput
                                        v-model:phone-number="phone"
                                        v-model:country-code="currentPhoneCode"
                                        :class="{ 'is-invalid': v$.phone.$error }"
                                        label="Mobile Number"
                                        required
                                        placeholder="07234 123 456"
                                        @blur="v$.phone.$touch"
                                        ref="phoneInput"
                                    />
                                    <div v-if="v$.phone.$error" class="invalid-feedback d-block">
                                        Please enter a valid phone number
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-primary" @click="submit">Submit</button>
                    </div>
                </div><!-- main body -->
            </div>
        </div>
    </div>
</template>

<script>

import { Modal } from 'bootstrap';
import { ref } from 'vue';
import api from "@/services/api";
import DropdownInput from '@/components/DropdownInput.vue';
import TextInput from '@/components/TextInput.vue';
import PhoneInput from '@/components/PhoneInput.vue';
import Flash from '@/components/Flash.vue';
import { getErrorMessage } from '@/utils/errorHandler';
import useVuelidate from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';

export default {
    props: {
        show: { type: Boolean, required: true },
    },

    emits: ["close", "role-set"],

    setup() {
        const externalResults = ref({});
        return {
            externalResults,
            v$: useVuelidate({ $externalResults: externalResults })
        }
    },
    validations () {
        let validations = {};

        validations.connect_user_role_id = { required };

        const role = this.roles.find((role) => role.id == this.connect_user_role_id)

        if (role?.is_dentist) {
            validations.dentist_id = { required };

            if (this.showEmailField) {
                validations.email = { email, required };
            }

            if (this.showPhoneField) {
                const phoneRegex = helpers.regex(/^[\d\s]+$/);
                const validatePhoneLength = helpers.withMessage(
                    'Phone number must be 10 digits, or 11 digits if starting with 0',
                    (value) => {
                        const digitsOnly = value.replace(/\s/g, '');
                        if (digitsOnly.startsWith('0')) {
                            return digitsOnly.length === 11;
                        } else {
                            return digitsOnly.length === 10;
                        }
                    }
                );
                validations.phone = { required, phoneRegex, validatePhoneLength };
            }
        }

        return validations;
    },
    data() {
        return {
            user_id: null,
            practiceDentists: [],
            roles: [],
            dentist_id: null,
            connect_user_role_id: '',
            selectedDentist: null,
            email: '',
            phone: '',
            currentPhoneCode: '',
            showEmailField: false,
            showPhoneField: false,
            showDentistField: false,
            flashMessage: '',
        }
    },
    watch: {
        connect_user_role_id(newRoleId) {
            const role = this.roles.find((role) => role.id == newRoleId);
            this.showDentistField = role.is_dentist;

            if (this.showDentistField) {
                return;
            }

            this.dentist_id = null;
            this.selectedDentist = null;
            this.email = '';
            this.phone = '';
            this.showEmailField = false;
            this.showPhoneField = false;
        },
        dentist_id(newDentistId) {
            const selectedDentist = this.practiceDentists.find((dentist) => dentist.id == newDentistId) || null;
            this.selectedDentist = selectedDentist;

            this.showEmailField = !selectedDentist?.email;
            this.showPhoneField = !selectedDentist?.phone;

            this.email = selectedDentist?.email || '';

            // Handle phone number parsing using PhoneInput component
            if (selectedDentist?.phone) {
                this.$nextTick(() => {
                    if (this.$refs.phoneInput) {
                        const phoneData = this.$refs.phoneInput.parseInternationalPhone(selectedDentist.phone);
                        this.phone = phoneData.localNumber;
                        this.currentPhoneCode = phoneData.countryCode || '';
                    } else {
                        // Fallback if component not ready
                        this.phone = selectedDentist.phone;
                        this.currentPhoneCode = '';
                    }
                });
            } else {
                this.phone = '';
                this.currentPhoneCode = '';
            }
        },
    },
    mounted() {
        this.detailsModalInstance = new Modal(this.$refs.confirmDetails, {});

        this.$watch("show", (newVal) => {
            if (newVal) {
                this.detailsModalInstance.show()
            } else {
                this.detailsModalInstance.hide()
            }
        }, { immediate: true });

        this.$refs.confirmDetails.addEventListener("hidden.bs.modal", () => {
            if (this.show) {
                this.$emit("close")
            }
        });

        this.fetchDentists();
        this.fetchRoles();
    },
    methods: {
        closeModal() {
            this.detailsModalInstance.hide();
            this.$emit("close");
        },
        submit: async function () {
            this.flashMessage = '';
            await this.v$.$validate();

            if (this.v$.$invalid) {
                return;
            }

            const formattedPhone = this.$refs.phoneInput ?
                this.$refs.phoneInput.getFormattedPhoneForSubmission() :
                this.phone;

            try {
                await api.post('/api/v1/userAdmin/confirm-details', {
                    connect_user_role_id: this.connect_user_role_id,
                    dentist_id: this.dentist_id,
                    email: this.email,
                    phone: formattedPhone,
                    country_code: this.currentPhoneCode ? `+${this.currentPhoneCode}` : '',
                });
                this.detailsModalInstance.hide();
                this.$emit("role-set", this.selectedRole)
            } catch (e) {
                this.flashMessage = getErrorMessage(e, 'Failed to confirm your details. Please try again.');
            }
        },
        fetchDentists: async function() {
            const practiceId = this.$store.state.currentPracticeId;
            try {
                const res = await api.get(`/api/v1/practices/${practiceId}/dentists`);
                this.practiceDentists = res.data.data.map((dentist) => {
                    return {
                        id: dentist.id,
                        label: `${dentist.title} ${dentist.first_name} ${dentist.surname}`,
                        email: dentist.email,
                        phone: dentist.phone,
                    }
                })
            } catch (e) {
                console.log(e);
            }
        },
        fetchRoles: async function() {
            try {
                const response = await api.get('/api/v1/roles');
                const roleData = response.data.roles;
                this.roles = roleData;
            } catch (error) {
                console.error('Failed to get all roles', error);
            }
        }
    },
    components: {
        DropdownInput,
        TextInput,
        PhoneInput,
        Flash,
    },
}
</script>

<style scoped lang="scss">
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.3s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    .fade-enter-to,
    .fade-leave-from {
        opacity: 1;
    }

    .modal-content {
        overflow:hidden;
        flex-direction: row;

        .btn-close {
            padding: 20px;
            right: 0;
            position: absolute;
        }

        .modal-sidebar {
        width: 35%;
        padding: 8px;
        background-color: rgb(186, 237, 171);
        position: relative;

            .modal-header {
                border-bottom: 0;

                    .modal-title {
                    border-bottom:1px solid #3d8826;
                }
            }
            
            &:after {
                content: '';
                position: absolute;
                top: 50%;
                right: -20px;
                transform: translateY(-50%);
                width: 0;
                height: 0;
                border-left: 20px solid rgb(186, 237, 171);
                border-top: 15px solid transparent;
                border-bottom: 15px solid transparent;
            }
        }
        .modal-main {
            width:60%;
            padding-left: 6%; 

            .modal-body {
                margin-top:30px;
                width: 100%;

                & > div {
                    animation: fadeInUp 0.5s ease-out;
                    animation-fill-mode: both;
                    
                    &:nth-child(1) {
                        animation-delay: 0.1s;
                    }
                }

                ::v-deep .input-group {
                    transition: box-shadow 0.3s ease;
                    
                    
                    &:focus-within {
                        box-shadow: 0 2px 8px rgba(127, 176, 105, 0.2);
                    }
                }

                .details-wrapper {
                    
                    & > div {
                        animation: fadeInUp 0.4s ease-out;
                        animation-fill-mode: both;
                        
                        &:nth-child(1) {
                            animation-delay: 0.1s;
                        }
                        
                        &:nth-child(2) {
                            animation-delay: 0.2s;
                        }
                        
                        &:nth-child(3) {
                            animation-delay: 0.3s;
                        }
                        
                        &:last-child {
                            margin-bottom: 0;
                        }
                    }
                }
            }
        }
    }


    @include media-breakpoint-down(lg)
    {
        .modal-content {
            flex-direction: column;
            
                .modal-sidebar {
                    width: 100%;
                    
                    &:after {
                        top: auto;
                        bottom: -20px;
                        right: 50%;
                        transform: translateX(50%);
                        border-left: 15px solid transparent;
                        border-right: 15px solid transparent;
                        border-top: 20px solid rgb(186, 237, 171);
                        border-bottom: none;
                    }
                }

                .modal-main {
                    width:100%;
                    padding-left: 0; 
                    padding-top: 30px;
                    .modal-body {
                        margin-top:0;
                    }
                }
        }
        
    }

</style>